import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  CreateFormingSessionModel,
  GetFormingSessionByIdResponse,
  ProblemDetails,
  SimulateArticlePlacementParams,
  SimulateArticlePlacementResponse
} from '../../models';

import { useCustomAppClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Get a plan by its id
 */
export const useGetFormingSessionByIdHook = () => {
        const getFormingSessionById = useCustomAppClient<GetFormingSessionByIdResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getFormingSessionById(
          {url: `/formingsessions/${id}`, method: 'GET', signal
    },
          );
        }, [getFormingSessionById])
      }




export const getGetFormingSessionByIdQueryKey = (id: string,) => {
    return [
    `/formingsessions/${id}`
    ] as const;
    }


export const useGetFormingSessionByIdQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFormingSessionByIdQueryKey(id);

  const getFormingSessionById =  useGetFormingSessionByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>> = ({ signal }) => getFormingSessionById(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFormingSessionByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>>
export type GetFormingSessionByIdQueryError = ErrorType<ProblemDetails>


export function useGetFormingSessionById<TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFormingSessionById<TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFormingSessionById<TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetFormingSessionById<TData = Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFormingSessionByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetFormingSessionByIdQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Create a new formingsession
 */
export const useCreateFormingSessionHook = () => {
        const createFormingSession = useCustomAppClient<GetFormingSessionByIdResponse>();

        return useCallback((
    createFormingSessionModel: CreateFormingSessionModel,
 signal?: AbortSignal
) => {


        return createFormingSession(
          {url: `/formingsessions`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createFormingSessionModel, signal
    },
          );
        }, [createFormingSession])
      }




export const getCreateFormingSessionMutationKey = () => ['createFormingSession'] as const;

export const useCreateFormingSessionMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>, TError,CreateFormingSessionMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>, TError,CreateFormingSessionMutationVariables, TContext> => {

const mutationKey = getCreateFormingSessionMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createFormingSession =  useCreateFormingSessionHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>, CreateFormingSessionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createFormingSession(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateFormingSessionMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>>
    export type CreateFormingSessionMutationBody = CreateFormingSessionModel
    export type CreateFormingSessionMutationError = ErrorType<ProblemDetails>
    export type CreateFormingSessionMutationVariables = {data: CreateFormingSessionModel}

    export const useCreateFormingSession = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>, TError,CreateFormingSessionMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateFormingSessionHook>>>,
        TError,
        CreateFormingSessionMutationVariables,
        TContext
      > => {
      return useMutation(useCreateFormingSessionMutationOptions(options), queryClient);
    }
    /**
 * Simulate placing multiple articles in a forming session for testing and demonstration purposes
 */
export const useSimulateArticlePlacementHook = () => {
        const simulateArticlePlacement = useCustomAppClient<SimulateArticlePlacementResponse>();

        return useCallback((
    simulateArticlePlacementBody: string[],
    params?: SimulateArticlePlacementParams,
 signal?: AbortSignal
) => {


        return simulateArticlePlacement(
          {url: `/formingsessions/simulate-articles`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: simulateArticlePlacementBody,
        params, signal
    },
          );
        }, [simulateArticlePlacement])
      }




export const getSimulateArticlePlacementMutationKey = () => ['simulateArticlePlacement'] as const;

export const useSimulateArticlePlacementMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>, TError,SimulateArticlePlacementMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>, TError,SimulateArticlePlacementMutationVariables, TContext> => {

const mutationKey = getSimulateArticlePlacementMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const simulateArticlePlacement =  useSimulateArticlePlacementHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>, SimulateArticlePlacementMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  simulateArticlePlacement(data,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SimulateArticlePlacementMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>>
    export type SimulateArticlePlacementMutationBody = string[]
    export type SimulateArticlePlacementMutationError = ErrorType<ProblemDetails>
    export type SimulateArticlePlacementMutationVariables = {data: string[];params?: SimulateArticlePlacementParams}

    export const useSimulateArticlePlacement = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>, TError,SimulateArticlePlacementMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useSimulateArticlePlacementHook>>>,
        TError,
        SimulateArticlePlacementMutationVariables,
        TContext
      > => {
      return useMutation(useSimulateArticlePlacementMutationOptions(options), queryClient);
    }
